import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { cn } from '@/shared/utils/utils';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableProps<T> {
  /** Data-driven mode. Omit all three to use the compositional mode instead. */
  columns?: Column<T>[];
  data?: T[];
  rowKey?: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  className?: string;
  /**
   * Loading region: paints skeleton rows and puts `aria-busy` on the scroll
   * wrapper so the four states (loading / empty / error / data) are all
   * reachable without every page re-inventing it. Additive — callers that do
   * not pass it behave exactly as before.
   */
  loading?: boolean;
  /** Compositional mode: <Table><TableHeader/><TableBody/></Table>. */
  children?: ReactNode;
}

// `tabular-nums` on its own only aligns the digits; the ledger look comes from
// the mono face too, so a right-aligned column is figure-first everywhere.
const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right font-mono tabular-nums' };

/**
 * Generic, presentation-only table. Data fetching belongs to TanStack Query.
 *
 * Supports two APIs:
 *  1. Data-driven — pass `columns` / `data` / `rowKey` (the original contract).
 *  2. Compositional — pass `<TableHeader>` / `<TableBody>` children, the
 *     shadcn-style API the Manufacturing pages were authored against.
 * Both render the same shell, so existing callers are unaffected.
 *
 * Sticky header: `sticky top-0` pins to the nearest vertical scroll port, which
 * is this wrapper — and the wrapper is content-sized, so on an uncapped table
 * the utility is inert (it does not break anything, it just does nothing). A
 * screen that wants the header to actually stay put passes a height cap on the
 * wrapper: `<Table className="max-h-[70vh]">`.
 */
export function Table<T>({ columns, data, rowKey, onRowClick, empty, className, loading, children }: TableProps<T>) {
  // Compositional mode — the caller supplies the row markup itself.
  if (!columns || !data || !rowKey) {
    return (
      <div
        className={cn('overflow-x-auto rounded-card border border-border bg-card', className)}
        aria-busy={loading || undefined}
      >
        <table className="w-full border-collapse text-data-md">{children}</table>
      </div>
    );
  }

  return (
    <div
      className={cn('animate-fade-in overflow-x-auto rounded-card border border-border bg-card', className)}
      aria-busy={loading || undefined}
    >
      <table className="w-full border-collapse text-data-md">
        <thead>
          <tr className="bg-muted">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={cn(
                  // The border sits on the cell, not the row: a sticky cell that
                  // loses its row background keeps its hairline this way.
                  'sticky top-0 z-10 border-b border-border bg-muted px-4 py-2.5 text-label-caps uppercase text-on-surface-subtle',
                  c.align === 'right' && 'text-right',
                  c.align === 'center' && 'text-center',
                  c.className,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {loading ? (
            // Skeleton rows sized to the real row rhythm, not a spinner over an
            // empty table. No invented values — placeholders only.
            Array.from({ length: 5 }).map((_, ri) => (
              <tr key={`skeleton-${ri}`} aria-hidden>
                {columns.map((c) => (
                  <td key={c.key} className={cn('px-4 py-2.5', ALIGN[c.align ?? 'left'])}>
                    <span className="block h-3 animate-pulse rounded bg-surface-dim" />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-10 text-center text-body-md text-on-surface-subtle">
                {empty ?? 'No records.'}
              </td>
            </tr>
          ) : (
            data.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (event) => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onRowClick(row);
                  }
                } : undefined}
                className={cn(
                  'bg-card transition-colors hover:bg-muted/50',
                  onRowClick &&
                    'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                )}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cn('px-4 py-2.5 text-on-surface', ALIGN[c.align ?? 'left'], c.className)}>
                    {c.render ? c.render(row) : (row as Record<string, ReactNode>)[c.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Compositional primitives ────────────────────────────────────────────────
// Thin, styled wrappers over the native table elements. These exist so pages
// written against the shadcn-style API (<TableHeader>, <TableRow>, …) render
// correctly here without being rewritten. Purely additive.

export function TableHeader({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('bg-muted', className)} {...props} />;
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('divide-y divide-border', className)} {...props} />;
}

export function TableFooter({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tfoot
      className={cn('border-t border-border bg-muted font-semibold text-on-surface', className)}
      {...props}
    />
  );
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn('bg-card transition-colors hover:bg-muted/50', className)}
      {...props}
    />
  );
}

export function TableHead({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cn(
        'sticky top-0 z-10 border-b border-border bg-muted px-4 py-2.5 text-left text-label-caps uppercase text-on-surface-subtle',
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('px-4 py-2.5 text-on-surface', className)} {...props} />;
}

export function TableCaption({ className, ...props }: HTMLAttributes<HTMLTableCaptionElement>) {
  return <caption className={cn('py-2 text-body-sm text-on-surface-subtle', className)} {...props} />;
}
